export interface PenaltyRecord {
  id: string;
  memberId: string;
  eventId: string;
  eventName: string;
  eventDate: string;
  type: 'late_cancel' | 'no_show';
  reason: string;
  timestamp: string;
  adminId?: string; // ID of admin who assigned penalty (for no-shows)
}

export interface MemberPenalty {
  memberId: string;
  totalDots: number;
  activePenaltyLevel: 0 | 1 | 2 | 3; // 0: no penalty, 1: 2 weeks, 2: 4 weeks, 3: 2 months
  penaltyStartDate?: string;
  penaltyEndDate?: string;
  records: PenaltyRecord[];
}

export interface PenaltyLevel {
  level: number;
  dotsRequired: number;
  durationWeeks: number;
  signupHoursBeforeEvent: number;
  description: string;
}

export const PENALTY_LEVELS: PenaltyLevel[] = [
  {
    level: 0,
    dotsRequired: 0,
    durationWeeks: 0,
    signupHoursBeforeEvent: 72, // Normal signup time
    description: 'No penalty'
  },
  {
    level: 1,
    dotsRequired: 3,
    durationWeeks: 2,
    signupHoursBeforeEvent: 4,
    description: '2 weeks penalty - signup only 4 hours before event'
  },
  {
    level: 2,
    dotsRequired: 4,
    durationWeeks: 4,
    signupHoursBeforeEvent: 4,
    description: '4 weeks penalty - signup only 4 hours before event'
  },
  {
    level: 3,
    dotsRequired: 5,
    durationWeeks: 8, // 2 months
    signupHoursBeforeEvent: 4,
    description: '2 months penalty - signup only 4 hours before event'
  }
];

export function calculatePenaltyLevel(totalDots: number): PenaltyLevel {
  if (totalDots >= 5) return PENALTY_LEVELS[3];
  if (totalDots >= 4) return PENALTY_LEVELS[2];
  if (totalDots >= 3) return PENALTY_LEVELS[1];
  return PENALTY_LEVELS[0];
}

export function isPenaltyActive(penalty: MemberPenalty): boolean {
  if (!penalty.penaltyEndDate) return false;
  return new Date() < new Date(penalty.penaltyEndDate);
}

export function canSignupToEvent(penalty: MemberPenalty | null, eventDate: string): {
  canSignup: boolean;
  reason?: string;
  hoursUntilSignup?: number;
} {
  if (!penalty || !isPenaltyActive(penalty)) {
    return { canSignup: true };
  }

  const penaltyLevel = calculatePenaltyLevel(penalty.totalDots);
  const eventDateTime = new Date(eventDate);
  const now = new Date();
  const hoursUntilEvent = (eventDateTime.getTime() - now.getTime()) / (1000 * 60 * 60);

  if (hoursUntilEvent <= penaltyLevel.signupHoursBeforeEvent) {
    return { canSignup: true };
  }

  const hoursUntilSignup = hoursUntilEvent - penaltyLevel.signupHoursBeforeEvent;
  
  return {
    canSignup: false,
    reason: `Due to penalty (${penalty.totalDots} dots), you can only sign up ${penaltyLevel.signupHoursBeforeEvent} hours before the event.`,
    hoursUntilSignup
  };
}

export function formatPenaltyReason(record: PenaltyRecord): string {
  switch (record.type) {
    case 'late_cancel':
      return 'Cancelled less than 3 hours before event start';
    case 'no_show':
      return 'Did not show up to event';
    default:
      return 'Unknown penalty reason';
  }
}

export function getPenaltyDisplayText(penalty: MemberPenalty, language: 'no' | 'en'): string {
  const level = calculatePenaltyLevel(penalty.totalDots);
  
  const translations = {
    no: {
      noPenalty: 'Ingen straff',
      dots: 'prikker',
      twoWeeks: '2 uker straff - påmelding kun 4 timer før arrangement',
      fourWeeks: '4 uker straff - påmelding kun 4 timer før arrangement',
      twoMonths: '2 måneder straff - påmelding kun 4 timer før arrangement'
    },
    en: {
      noPenalty: 'No penalty',
      dots: 'dots',
      twoWeeks: '2 weeks penalty - signup only 4 hours before event',
      fourWeeks: '4 weeks penalty - signup only 4 hours before event',
      twoMonths: '2 months penalty - signup only 4 hours before event'
    }
  };

  const t = translations[language];
  
  if (penalty.totalDots === 0) {
    return t.noPenalty;
  }

  const dotsText = `${penalty.totalDots} ${t.dots}`;
  
  switch (level.level) {
    case 1:
      return `${dotsText} - ${t.twoWeeks}`;
    case 2:
      return `${dotsText} - ${t.fourWeeks}`;
    case 3:
      return `${dotsText} - ${t.twoMonths}`;
    default:
      return dotsText;
  }
}
